// Hasta çalışma alanı "Su Takibi" sekmesi — analiz dili: slate50 + KPI satırı +
// sabit gövdeli kartlar. İçimlerin tek tek saatleri klinikle paylaşılmaz;
// yalnız günlük toplamlar. Kurallar: docs/design-principles.md §4
import React, { useEffect, useState } from "react";
import { Box, Chip, Skeleton, Typography, alpha } from "@mui/material";
import {
  WaterDropOutlined as WaterIcon,
  InsertChartOutlined as ChartIcon,
  FlagOutlined as GoalIcon,
} from "@mui/icons-material";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
} from "recharts";

import SectionCard from "../../molecules/SectionCard";
import KpiTile from "../../molecules/KpiTile";
import { ActionButton, EmptyState, FormTextField, InfoRow, SegmentedControl } from "../../atoms";
import { COLORS, TYPO, CARD_PAD, CARD_GAP, dashboardSectionSx } from "../../../styles/uiConstants";
import { useClinicTheme } from "../../../theme";
import { useAlert } from "../../../context/AlertProvider";
import { useHydrationSummaryMutation, useSetHydrationGoalMutation } from "../../../store/hydrationApi";
import useTerms from "../../../hooks/useTerms";

const BODY_HEIGHT = 320;
const AXIS_TICK = { fontSize: 12, fill: COLORS.slate500 };
const SK_HEIGHTS = [45, 70, 55, 85, 60, 95, 50];

const liters = (ml) => `${(ml / 1000).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} L`;
const dayLabel = (ymd) => new Date(`${ymd}T12:00:00`).toLocaleDateString("tr-TR", { day: "numeric", month: "short" });
const intervalLabel = (min) => `${(min / 60).toLocaleString("tr-TR")} saatte bir`;

function ChartTip({ active, payload, goalMl }) {
  if (!active || !payload?.length) return null;
  const d = payload[0].payload;
  return (
    <Box
      sx={{
        bgcolor: COLORS.white,
        border: `1px solid ${COLORS.slate200}`,
        borderRadius: 2,
        boxShadow: "0 8px 24px rgba(15,23,42,0.12)",
        px: 1.5,
        py: 1,
        minWidth: 120,
      }}
    >
      <Typography sx={{ fontSize: TYPO.caption, color: COLORS.slate500, mb: 0.5 }}>{d.label}</Typography>
      <Typography sx={{ fontSize: TYPO.body, fontWeight: 700, color: COLORS.slate800 }}>{liters(d.ml)}</Typography>
      <Typography sx={{ fontSize: TYPO.caption, color: d.goalMet ? COLORS.successDark : COLORS.slate500 }}>
        {d.goalMet ? "Hedefe ulaştı" : `Hedefin ${liters(Math.max(0, goalMl - d.ml))} altında`}
      </Typography>
    </Box>
  );
}

// flush: gövde iç boşluksuz (InfoRow kendi CARD_PAD boşluğunu taşır)
const card = (title, subtitle, icon, children, action, flush = false) => (
  <SectionCard
    title={title}
    subtitle={subtitle}
    icon={icon}
    action={action}
    sx={{ ...dashboardSectionSx, display: "flex", flexDirection: "column" }}
  >
    <Box sx={{ p: flush ? 0 : CARD_PAD, height: BODY_HEIGHT, display: "flex", flexDirection: "column", minHeight: 0 }}>
      {children}
    </Box>
  </SectionCard>
);

/**
 * @param {string} patientId
 * @param {string} token
 * @param {boolean} canManage - klinik günlük hedefi belirleyebilir
 */
/**
 * @param {boolean} canManage - klinik hedefi buradan koyabilir mi
 * @param {boolean} [planManaged] - hedef Beslenme Planı'nda yönetilir (Beslenme › Takip içinde):
 *   hedef salt okunur, açıklama plana yönlendirir
 */
export default function HydrationTab({ patientId, token, canManage, planManaged = false }) {
  const terms = useTerms();
  const { palette } = useClinicTheme();
  const { showSuccess, showError } = useAlert();
  const [days, setDays] = useState("7");
  const [load, { data, isLoading }] = useHydrationSummaryMutation();
  const [saveGoal, { isLoading: saving }] = useSetHydrationGoalMutation();
  const [goalInput, setGoalInput] = useState("");

  const refresh = () => token && patientId && load({ token, patientId, days: Number(days) });
  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, patientId, days]);

  const s = data?.data;
  const loading = isLoading || !s;
  useEffect(() => {
    if (s?.plan) setGoalInput(String(s.plan.goalMl));
  }, [s?.plan?.goalMl]); // eslint-disable-line react-hooks/exhaustive-deps

  const submitGoal = async (goalMl) => {
    try {
      await saveGoal({ token, patientId, goalMl }).unwrap();
      showSuccess(goalMl === null ? `Hedef ${terms.patientDatLower} bırakıldı` : "Günlük su hedefi kaydedildi");
      refresh();
    } catch (e) {
      showError(e?.data?.message || "Hedef kaydedilemedi");
    }
  };

  const chartData = (s?.days || []).map((d) => ({ ...d, label: dayLabel(d.date) }));
  const anyIntake = chartData.some((d) => d.ml > 0);
  const average = chartData.length ? Math.round(chartData.reduce((sum, d) => sum + d.ml, 0) / chartData.length) : 0;
  const reminders = s?.plan?.reminders;
  const periodLabel = `Son ${days} gün`;

  const periodSwitch = (
    <SegmentedControl
      value={days}
      onChange={setDays}
      ariaLabel="Dönem"
      options={[
        { value: "7", label: "7 gün" },
        { value: "30", label: "30 gün" },
      ]}
    />
  );

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: CARD_GAP }}>
      {/* Özet */}
      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr 1fr", lg: "repeat(4, 1fr)" } }}>
        <KpiTile
          loading={loading}
          label="Bugün"
          value={s ? liters(s.today.ml) : ""}
          hint={s ? `Hedef ${liters(s.plan.goalMl)}` : ""}
        />
        <KpiTile
          loading={loading}
          label="Hedefe ulaşılan gün"
          value={s ? `${s.goalMetDays}/${s.days.length}` : ""}
          hint={periodLabel}
        />
        <KpiTile loading={loading} label="Günlük ortalama" value={liters(average)} hint={periodLabel} />
        <KpiTile
          loading={loading}
          label="Hatırlatma"
          value={reminders?.enabled ? "Açık" : "Kapalı"}
          hint={
            reminders?.enabled
              ? `${reminders.start}–${reminders.end} · ${intervalLabel(reminders.intervalMin)}`
              : `${terms.patient} telefonundan açabilir`
          }
        />
      </Box>

      <Box sx={{ display: "grid", gap: CARD_GAP, gridTemplateColumns: { xs: "1fr", lg: "1.4fr 1fr" } }}>
        {card(
          "Günlük tüketim",
          `${periodLabel} · çizgi günlük hedef`,
          <ChartIcon sx={{ fontSize: 18 }} />,
          loading ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "flex-end", gap: 2, px: 1 }}>
              {SK_HEIGHTS.map((h, i) => (
                <Skeleton key={i} variant="rounded" sx={{ flex: 1, height: `${h}%`, borderRadius: "4px 4px 0 0" }} />
              ))}
            </Box>
          ) : !anyIntake ? (
            <Box sx={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center" }}>
              <EmptyState
                variant="illustrated-sm"
                icon={<WaterIcon />}
                title="Bu dönemde su kaydı yok"
                description={`${terms.patient} uygulamada içtiği bardakları işaretledikçe günlük toplamlar burada görünür.`}
                sx={{ py: 0 }}
              />
            </Box>
          ) : (
            <Box sx={{ flex: 1, minHeight: 0 }} role="img" aria-label={`Günlük su tüketimi, ${periodLabel.toLowerCase()}`}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={chartData} barSize={days === "7" ? 24 : 10} margin={{ top: 8, right: 4, left: -8, bottom: 0 }}>
                  <CartesianGrid stroke={COLORS.slate100} vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={AXIS_TICK}
                    axisLine={{ stroke: COLORS.slate200 }}
                    tickLine={false}
                    interval={days === "7" ? 0 : "preserveStartEnd"}
                  />
                  <YAxis
                    tick={AXIS_TICK}
                    axisLine={false}
                    tickLine={false}
                    tickFormatter={(v) => `${(v / 1000).toLocaleString("tr-TR")} L`}
                  />
                  <Tooltip
                    cursor={{ fill: alpha(COLORS.slate200, 0.4) }}
                    content={<ChartTip goalMl={s.plan.goalMl} />}
                  />
                  <ReferenceLine y={s.plan.goalMl} stroke={COLORS.slate400} ifOverflow="extendDomain" />
                  <Bar dataKey="ml" name="Su" fill={palette.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </Box>
          ),
          periodSwitch
        )}

        {card(
          "Günlük hedef",
          s?.dietPlan
            ? `Beslenme planından: ${s.dietPlan.title || "Beslenme planı"} — ${terms.patientLower} değiştiremez`
            : s?.plan?.goalSetBy === "clinic"
              ? `Klinik belirledi — ${terms.patientLower} değiştiremez`
              : `${terms.patient} kendi belirliyor`,
          <GoalIcon sx={{ fontSize: 18 }} />,
          <Box>
            <InfoRow label="Günlük hedef">
              {loading ? (
                <Skeleton width={120} />
              ) : canManage ? (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <FormTextField
                    variant="standard"
                    type="number"
                    value={goalInput}
                    onChange={(e) => setGoalInput(e.target.value)}
                    inputProps={{ min: 500, max: 6000, step: 250, "aria-label": "Günlük hedef (ml)" }}
                    sx={{ maxWidth: 96 }}
                    palette={palette}
                  />
                  <Typography sx={{ fontSize: TYPO.label, color: COLORS.slate500 }}>ml</Typography>
                  <ActionButton
                    variant="save"
                    compact
                    label="Kaydet"
                    loading={saving}
                    disabled={!goalInput || Number(goalInput) === s.plan.goalMl}
                    onClick={() => submitGoal(Number(goalInput))}
                    palette={palette}
                  />
                </Box>
              ) : (
                <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>{liters(s.plan.goalMl)}</Typography>
              )}
            </InfoRow>
            <InfoRow label="Belirleyen">
              {loading ? (
                <Skeleton width={100} />
              ) : (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  <Chip
                    size="small"
                    label={s.plan.goalSetBy === "clinic" ? "Klinik belirledi" : `${terms.patient} belirledi`}
                    sx={{
                      height: 22,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      bgcolor: s.plan.goalSetBy === "clinic" ? alpha(palette.primary, 0.08) : COLORS.slate100,
                      color: s.plan.goalSetBy === "clinic" ? palette.primary : COLORS.slate600,
                    }}
                  />
                  {canManage && s.plan.goalSetBy === "clinic" ? (
                    <ActionButton
                      variant="edit"
                      tone="outline"
                      compact
                      label={`${terms.patientDat} bırak`}
                      onClick={() => submitGoal(null)}
                    />
                  ) : null}
                </Box>
              )}
            </InfoRow>
            <InfoRow label="Bardak" noBorder>
              {loading ? (
                <Skeleton width={80} />
              ) : (
                <Typography sx={{ fontSize: TYPO.body, color: COLORS.slate700 }}>
                  {s.plan.glassMl ? `${s.plan.glassMl} ml` : "—"}
                </Typography>
              )}
            </InfoRow>
            <Typography sx={{ px: CARD_PAD, pt: 1, fontSize: TYPO.caption, color: COLORS.slate500 }}>
              {planManaged ? (
                <>
                  Günlük su hedefi Beslenme Planı&apos;nda belirlenir; plan aktifken {terms.patientGenLower} su takibine uygulanır.
                  Hatırlatma ve bardak boyunu {terms.patientLower} kendi telefonunda ayarlar.
                </>
              ) : (
                <>
                  Klinik hedef belirlediğinde {terms.patientLower} değiştiremez; “{terms.patientDat} bırak” ile hedef yeniden{" "}
                  {terms.patientDatLower} geçer. Hatırlatma ve bardak boyunu {terms.patientLower} kendi telefonunda ayarlar.
                </>
              )}
            </Typography>
          </Box>,
          null,
          true
        )}
      </Box>
    </Box>
  );
}
